/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-04
 */

import React from 'react';
import { API_BASE_URL, API_VERSION } from '../config/api';

/**
 * User detection result interface
 */
export interface UserDetectionResult {
  userExists: boolean;
  isLoading: boolean;
  error: string | null;
}

/**
 * True when checkUserExists failed because the backend rate limited the
 * check (429).
 */
export const isRateLimitedUserCheck = (error: unknown): boolean =>
  (error as { status?: number } | null)?.status === 429;

/**
 * Detects if a user exists using the dedicated user existence check endpoint.
 * This provides a reliable way to determine if a user account already exists
 * for a given email address, used primarily in invitation acceptance flows.
 */
export class UserDetectionService {
  /**
   * Check if a user exists for the given email address
   * @param email - Email address to check
   * @returns Promise<boolean> - True if user exists, false otherwise
   * @throws Error with a `status` on a non-2xx response (never treat a
   * failed check, e.g. a 429 rate limit, as "no account"); network errors
   * propagate
   */
  static async checkUserExists(email: string): Promise<boolean> {
    // Use the dedicated user existence check endpoint
    const encodedEmail = encodeURIComponent(email);
    const response = await fetch(
      `${API_BASE_URL}/api/${API_VERSION}/users/check-exists/?email=${encodedEmail}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }
    );

    if (!response.ok) {
      throw Object.assign(
        new Error(`User existence check failed with status ${response.status}`),
        { status: response.status }
      );
    }

    // Parse response
    const data = await response.json();

    // Return the exists boolean from the response
    return data.exists === true;
  }

  /**
   * Validate email format before checking existence
   * @param email - Email to validate
   * @returns boolean - True if email format is valid
   */
  static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}

/**
 * React hook for user detection with loading and error states
 * @param email - Email to check (optional)
 * @returns UserDetectionResult with userExists, isLoading, and error
 */
export const useUserDetection = (email?: string): UserDetectionResult => {
  const [result, setResult] = React.useState<UserDetectionResult>({
    userExists: false,
    isLoading: false,
    error: null,
  });

  const checkUser = React.useCallback(async (emailToCheck: string) => {
    if (!UserDetectionService.isValidEmail(emailToCheck)) {
      setResult({
        userExists: false,
        isLoading: false,
        error: 'Invalid email format',
      });
      return;
    }

    setResult(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const userExists =
        await UserDetectionService.checkUserExists(emailToCheck);
      setResult({
        userExists,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      setResult({
        userExists: false,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Detection failed',
      });
    }
  }, []);

  React.useEffect(() => {
    if (email && UserDetectionService.isValidEmail(email)) {
      checkUser(email);
    }
  }, [email, checkUser]);

  return {
    ...result,
    checkUser,
  } as UserDetectionResult & { checkUser: (email: string) => Promise<void> };
};
